// Phase 6 — Global unread-message poll. Drives:
//   • The notification badge on every shell's nav.
//   • The toast that pops when a brand-new message arrives while the
//     user is anywhere else in the app.
//
// One poll per shell, every 15 seconds. The Messages view does its
// own 5s poll while it's open, so we don't compete with it.

import { useEffect, useRef, useState } from "react";
import { useToast } from "./Toast";
import { api } from "../api";

const POLL_MS = 15_000;

export function useUnreadMessages({ active, onNavigate }) {
  // `active` is the current route key — silence toasts while the user
  // is already on the Messages view.
  const toast = useToast();
  const [unread, setUnread] = useState(0);
  // Track last seen count so we only toast on increases.
  const lastCountRef = useRef(null);

  useEffect(() => {
    let alive = true;
    async function tick() {
      try {
        const { count } = await api.unreadMessageCount();
        if (!alive) return;
        const previous = lastCountRef.current;
        setUnread(count);
        // Pop a toast the first time the count goes up after we've
        // seen at least one snapshot. We skip the initial load
        // (previous === null) so we don't toast every page refresh.
        if (previous !== null && count > previous && active !== "messages") {
          const delta = count - previous;
          toast.info(
            delta === 1
              ? "📨 You have a new message — open Messages to read it"
              : `📨 ${delta} new messages — open Messages to read them`,
            { duration: 6000 }
          );
        }
        lastCountRef.current = count;
      } catch { /* silent */ }
    }
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [active, onNavigate, toast]);

  return unread;
}
